import { useState } from "react";
import { Mail, Phone, MapPin, Clock, ShieldCheck, Send, CheckCircle2 } from "lucide-react";
import LegalLayout, { Sections, Rich } from "./LegalLayout";
import { COMPANY as C } from "./company";
import { useLegal, EN_TOPICS } from "./content";
import { sendContact } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border border-white/15 px-4 py-3 text-[0.92rem] text-[#f4f1eb] placeholder:text-white/30 focus:outline-none focus:border-[#B59560] transition-colors";

function SupportForm({ sp }) {
  const f = sp.form;
  const [form, setForm] = useState({ name: "", email: "", reference: "", topic: 0, message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await sendContact("support", form.name, form.email, {
        Topic: EN_TOPICS[form.topic],
        "Topic (visitor language)": sp.topics[form.topic],
        "Invoice / reference": form.reference,
        Message: form.message,
        Website: window.location.hostname,
      });
      setState("sent");
    } catch (err) {
      setState("error");
      setError(f.error);
    }
  };

  if (state === "sent") {
    return (
      <div className="border border-[#B59560]/50 bg-white/[0.03] p-6 flex gap-4" data-testid="support-sent">
        <CheckCircle2 className="h-5 w-5 text-[#B59560] shrink-0 mt-1" />
        <div>
          <p className="text-[#f4f1eb] font-medium mb-1">{f.sentTitle}</p>
          <p className="text-[0.9rem]">{f.sentBody(form.email)}</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="support-form">
      <input required className={inputCls} placeholder={f.name} value={form.name} onChange={set("name")} data-testid="support-name" />
      <input required type="email" className={inputCls} placeholder={f.email} value={form.email} onChange={set("email")} data-testid="support-email" />
      <input className={inputCls} placeholder={f.reference} value={form.reference} onChange={set("reference")} data-testid="support-reference" />
      <select className={`${inputCls} bg-[#0a0a0a]`} value={form.topic} onChange={(e) => setForm({ ...form, topic: Number(e.target.value) })} data-testid="support-topic">
        {sp.topics.map((t, i) => <option key={i} value={i}>{t}</option>)}
      </select>
      <textarea required rows={6} className={`${inputCls} sm:col-span-2 resize-y`} placeholder={f.message} value={form.message} onChange={set("message")} data-testid="support-message" />
      <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-4">
        <p className="text-[0.78rem] text-white/45 max-w-[26rem]">
          <Rich text={f.consent} />
        </p>
        <button
          type="submit"
          disabled={state === "sending"}
          data-testid="support-submit"
          className="inline-flex items-center gap-2 bg-[#f4f1eb] text-[#0a0a0a] font-mono-data text-[0.66rem] tracking-[0.2em] px-6 py-3.5 hover:bg-[#B59560] transition-colors disabled:opacity-60"
        >
          {state === "sending" ? f.sending : f.send} <Send className="h-3.5 w-3.5" />
        </button>
      </div>
      {state === "error" && <p className="sm:col-span-2 text-[0.85rem] text-red-300" role="alert"><Rich text={error} /></p>}
    </form>
  );
}

export default function Support() {
  const { support: sp } = useLegal();
  const CHANNELS = [
    { icon: Mail, label: sp.email, value: C.supportEmail, href: `mailto:${C.supportEmail}` },
    { icon: Phone, label: sp.phone, value: C.supportPhone, href: `tel:${C.supportPhoneHref}` },
    { icon: MapPin, label: C.servicePoint, value: C.address },
    { icon: Clock, label: sp.hoursLabel, value: sp.hours },
  ];
  const TOC = [
    { id: "contact", title: sp.tocContact },
    { id: "request", title: sp.tocRequest },
    ...sp.faq.map(({ id, title }) => ({ id, title })),
  ];
  return (
    <LegalLayout testid="support-page" kicker={sp.kicker} title={sp.title} intro={sp.intro} toc={TOC}>
      <section id="contact" className="scroll-mt-24 mb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {CHANNELS.map(({ icon: Icon, label, value, href }) => (
            <div key={label} className="bg-[#0a0a0a] p-5">
              <div className="flex items-center gap-2.5 mb-2">
                <Icon className="h-4 w-4 text-[#B59560]" />
                <span className="font-mono-data text-[0.6rem] tracking-[0.18em] uppercase text-white/45">{label}</span>
              </div>
              {href ? (
                <a href={href} className="text-[#f4f1eb] hover:text-[#B59560] transition-colors break-words">{value}</a>
              ) : (
                <p className="text-[#f4f1eb]">{value}</p>
              )}
            </div>
          ))}
        </div>
        <p className="mt-5 flex gap-3 text-[0.88rem]">
          <ShieldCheck className="h-4 w-4 text-[#B59560] shrink-0 mt-1" />
          <span>{sp.security}</span>
        </p>
      </section>

      <section id="request" className="scroll-mt-24 mb-14">
        <h2 className="font-gateway font-bold text-[1.25rem] md:text-[1.45rem] tracking-tight text-[#f4f1eb] mb-5">{sp.requestTitle}</h2>
        <SupportForm sp={sp} />
      </section>

      <p className="font-mono-data text-[0.62rem] tracking-[0.22em] text-[#B59560] mb-6">{sp.faqTitle}</p>
      <Sections sections={sp.faq} />
    </LegalLayout>
  );
}
